import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import type { ActivityEvent } from "@/hooks/use-activity";
import { formatRelativeTime } from "@/lib/upcoming-posts";

/**
 * Expandable card for autopilot run events (type "job", title starting
 * "Autopilot run"). v1 parses the server-folded `detail` string only — no
 * extra fetch. Per-platform gap numbers are not persisted in AgentJob.result
 * (verified in apps/worker/src/handlers/autopilot-run.ts), so the expanded
 * view shows the outcome, the skipped-reason/failed-stage line, the artifact
 * count, and the project link when one exists.
 */

type Outcome = "completed" | "skipped" | "failed" | "waiting";

const OUTCOME_CLS: Record<Outcome, string> = {
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  skipped: "bg-muted text-muted-foreground",
  failed: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  waiting: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-400",
};

function outcomeOf(title: string): Outcome {
  if (title.includes("waiting")) return "waiting";
  if (title.includes("skipped")) return "skipped";
  if (title.includes("failed")) return "failed";
  return "completed";
}

export function AutopilotRunCard({ event }: { event: ActivityEvent }) {
  const { t } = useTranslation("activity");
  const locale = useLocale();
  const [expanded, setExpanded] = useState(false);
  const outcome = outcomeOf(event.title);
  const badgeCls = OUTCOME_CLS[outcome];
  const outcomeLabel = {
    completed: t("outcome.completed"),
    skipped: t("outcome.skipped"),
    failed: t("outcome.failed"),
    waiting: t("outcome.waiting"),
  }[outcome];

  return (
    <div className={cn("rounded-xl border bg-card", outcome === "failed" && "border-destructive/40")}>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 rounded-xl px-3.5 py-3 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{event.title}</div>
          {!expanded && event.detail && (
            <div className="truncate text-xs text-muted-foreground">{event.detail}</div>
          )}
        </div>
        <time
          dateTime={event.at}
          title={formatRelativeTime(new Date(event.at), locale)}
          className="flex-shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground"
        >
          {formatDate(new Date(event.at), locale, { hour: "numeric", minute: "2-digit" })}
        </time>
        <ChevronDown
          size={14}
          className={cn(
            "mt-0.5 flex-shrink-0 text-muted-foreground transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>

      {expanded && (
        <div className="space-y-2 border-t px-3.5 py-3">
          <span
            className={cn("inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium", badgeCls)}
          >
            {outcomeLabel}
          </span>
          {event.detail && (
            <p className="text-sm text-muted-foreground">
              {outcome === "skipped" ? t("outcome.skippedDetail", { detail: event.detail }) : event.detail}
            </p>
          )}
          {event.href && (
            <Link to={event.href} className="block text-sm text-primary hover:underline">
              {t("outcome.viewProject")}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
